export const adminSections = [
  { key: "features", label: "Features", description: "Module und Funktionen freischalten" },
  {
    key: "knowledge",
    label: "Wissensdatenbank",
    description: "Wissen veröffentlichen und freigeben",
  },
  { key: "users", label: "Nutzer", description: "Nutzer und Rollen verwalten" },
  { key: "products", label: "Produkte", description: "Produkt- und Leistungskatalog verwalten" },
  { key: "projects", label: "Projekte", description: "Projektvorlagen verwalten" },
  { key: "crm", label: "CRM", description: "Kunden- und Vertriebsverwaltung konfigurieren" },
  {
    key: "bem",
    label: "BEM",
    description: "BEM-Verfahren, Vorlagen und Aufbewahrung konfigurieren",
  },
  { key: "marketing", label: "Marketing", description: "Marketingvorlagen verwalten" },
  {
    key: "surveys",
    label: "Befragungen",
    description: "LamaPoll-Verbindungen verwalten",
  },
  { key: "email", label: "E-Mail", description: "E-Mail-Eingang und Versand konfigurieren" },
  {
    key: "integrations",
    label: "Integrationen",
    description: "Externe Dienste verbinden und prüfen",
  },
  {
    key: "file-explorer",
    label: "Datei-Explorer",
    description: "Gemeinsame Dateiablagen verwalten",
  },
  { key: "ideas", label: "Ideen", description: "Eingereichte Verbesserungsideen bearbeiten" },
  { key: "audit", label: "Auditprotokoll", description: "Änderungen nachvollziehen" },
  { key: "tenants", label: "Mandanten", description: "Mandanten einsehen und verwalten" },
  {
    key: "technical-administration",
    label: "Technische Administration",
    description: "Betrieb und technische Dokumentation",
  },
  { key: "security", label: "Sicherheit", description: "Sicherheits- und Demodatenverwaltung" },
] as const;

export type AdminSectionKey = (typeof adminSections)[number]["key"];

export function visibleAdminSections(features: Record<string, boolean>, tenantKind?: string) {
  if (features.admin !== true) return [];
  return adminSections.filter((section) => {
    switch (section.key) {
      case "features":
      case "users":
      case "email":
      case "integrations":
        return features.adminIntegrations === true;
      case "bem":
        return features.bemManage === true;
      case "knowledge":
        return features.knowledgeAdmin === true;
      case "products":
      case "crm":
        return features.adminIntegrations === true && features.crm === true;
      case "projects":
        return features.adminIntegrations === true && features.projects === true;
      case "marketing":
        return features.adminIntegrations === true && features.marketing === true;
      case "surveys":
        return features.adminIntegrations === true && features.surveys === true;
      case "file-explorer":
        return features.adminIntegrations === true && tenantKind === "group";
      case "audit":
        return features.adminAudit === true;
      case "tenants":
        return features.adminTenants === true;
      case "technical-administration":
        return features.documentation === true;
      case "ideas":
      case "security":
        return true;
    }
  });
}

export function adminSectionPath(key: AdminSectionKey): string {
  return key === "technical-administration" ? "/dokumentation" : `/administration/${key}`;
}

export function isAdministrationPath(pathname: string): boolean {
  return (
    pathname === "/administration" ||
    pathname.startsWith("/administration/") ||
    pathname === "/dokumentation"
  );
}

export function adminSectionForLocation(
  pathname: string,
  search: string,
  features: Record<string, boolean>,
  tenantKind?: string,
): (typeof adminSections)[number] | undefined {
  if (!isAdministrationPath(pathname)) return undefined;
  const visibleSections = visibleAdminSections(features, tenantKind);
  const legacyKey = new URLSearchParams(search).get("tab");
  const requestedKey =
    pathname === "/dokumentation"
      ? "technical-administration"
      : pathname.startsWith("/administration/") && pathname !== "/administration/"
        ? pathname.slice("/administration/".length)
        : legacyKey === "datei-explorer"
          ? "file-explorer"
          : legacyKey === "technische-administration"
            ? "technical-administration"
            : legacyKey;
  return visibleSections.find((section) => section.key === requestedKey) ?? visibleSections[0];
}
